import { z } from 'zod';

/**
 * The ONE list of customer_org.organization_type values.
 *
 * This module is deliberately a leaf: it imports nothing but zod. The schema
 * is a runtime value consumed at module-initialization time (inside
 * `z.object({ ... })` bodies), and `dto/customer.ts` sits in an import cycle
 * with `dto/seller.ts` and `dto/client-profile.ts`. Defining it here means
 * every module can reach the canonical value without joining that cycle,
 * where whichever module initialized first would see `undefined`.
 *
 * Everything else derives from this: `customerOrganizationTypeSchema` and
 * `CustomerOrganizationType` below, the `OrgType` axis in `org-features.ts`,
 * and every narrower subset (`customerCreateSchema.organizationType` in
 * customer.ts, `brokerClientOrgTypeSchema` in brokerage.ts). Never re-type
 * these literals inline — a hand-written union does not widen when a value is
 * added here, so it keeps compiling while silently no longer matching reality.
 * That is exactly how adding a fourth org type would otherwise ship half-done.
 *
 * Mirrors two CHECK constraints in db/schema/1171: on
 * `customer_org.organization_type` and on `referral_code.organization_type`.
 * Adding a value here without adding it to both, and to
 * `provision_customer_org()`, creates a type the database refuses.
 *
 * The OrgType x feature matrix test in
 * `packages/shared/src/__tests__/org-features.test.ts` forces an explicit
 * answer for every feature when a value is added here. Note also that
 * `clientAuth.ts` resolves an unrecognized DB string to `null`, which denies
 * every org-gated feature — so a value reaching the database before the API
 * knows about it fails closed rather than open.
 */
export const CUSTOMER_ORGANIZATION_TYPES = ['business', 'individual', 'broker', 'reseller'] as const;

/**
 * SIL-2533: org types the consumers (read paths, gates) already understand but
 * that NO write path may create yet, because the producer migration (1271:
 * customer_org / referral_code CHECKs + provision_customer_org) is applied only
 * after the consumers are deployed. Every create route and form derives its
 * offered set by excluding these. Emptying this list is the follow-up that
 * ships after 1271 is applied in prod - not before: an invite for a type the
 * database refuses sends the Cognito invite email first and then fails.
 */
export const NOT_YET_PRODUCIBLE_ORG_TYPES = ['reseller'] as const;
export type NotYetProducibleOrgType = (typeof NOT_YET_PRODUCIBLE_ORG_TYPES)[number];

/**
 * The ONE normalizer for a raw customer_org.organization_type string (clientAuth
 * and the MCP auth path both use it, so the two cannot drift). Anything not in
 * the canonical list - including null/undefined - resolves to null, which
 * denies every org-gated feature (fail-closed).
 */
export function normalizeOrgType(raw: unknown): CustomerOrganizationType | null {
  return typeof raw === 'string' && (CUSTOMER_ORGANIZATION_TYPES as readonly string[]).includes(raw)
    ? (raw as CustomerOrganizationType)
    : null;
}

export const customerOrganizationTypeSchema = z.enum(CUSTOMER_ORGANIZATION_TYPES);
export type CustomerOrganizationType = z.infer<typeof customerOrganizationTypeSchema>;
